import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AudioWaveform,
  Plus,
  Music2,
  ListMusic,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Copy,
  Clock,
  Loader2,
  Library,
  FileText,
  Zap,
  X,
  GripVertical
} from "lucide-react";
import { toast } from "sonner";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_KEYS = ["C", "C#", "Db", "D", "D#", "Eb", "E", "F", "F#", "Gb", "G", "G#", "Ab", "A", "A#", "Bb", "B"];
const GENRES = ["Adoración", "Alabanza", "Balada", "Rock", "Pop", "Gospel", "Himno", "Contemporáneo", "Otro"];
const SECTION_TYPES = ["Intro", "Verse", "Pre-Chorus", "Chorus", "Bridge", "Outro", "Solo", "Instrumental", "Breakdown"];
const TIME_SIGNATURES = ["4/4", "3/4", "6/8", "2/4", "12/8"];

const LibraryPage = () => {
  const { getAuthHeaders } = useAuth();
  
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterKey, setFilterKey] = useState("all");
  const [filterGenre, setFilterGenre] = useState("all");
  
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingSong, setEditingSong] = useState(null);
  
  const [formData, setFormData] = useState({
    title: "",
    artist: "",
    original_key: "C",
    bpm: "",
    time_signature: "4/4",
    genre: "Adoración",
    energy_level: 5,
    notes: "",
    lyrics: "",
    duration_minutes: "",
    sections: [],
    tags: []
  });

  const fetchSongs = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/library/songs`, {
        headers: getAuthHeaders()
      });
      setSongs(response.data);
    } catch (error) {
      console.error("Error fetching songs:", error);
      toast.error("Error al cargar la biblioteca");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchSongs();
  }, [fetchSongs]);

  const resetForm = () => {
    setFormData({
      title: "",
      artist: "",
      original_key: "C",
      bpm: "",
      time_signature: "4/4",
      genre: "Adoración",
      energy_level: 5,
      notes: "",
      lyrics: "",
      duration_minutes: "",
      sections: [],
      tags: []
    });
    setEditingSong(null);
  };

  const addSection = () => {
    setFormData({
      ...formData,
      sections: [...formData.sections, { name: "Verse", bars: 8, notes: "" }]
    });
  };

  const updateSection = (index, field, value) => {
    const newSections = [...formData.sections];
    newSections[index] = { ...newSections[index], [field]: value };
    setFormData({ ...formData, sections: newSections });
  };

  const removeSection = (index) => {
    setFormData({
      ...formData,
      sections: formData.sections.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error("El título es requerido");
      return;
    }
    
    const payload = {
      ...formData,
      bpm: formData.bpm ? parseInt(formData.bpm) : null,
      duration_minutes: formData.duration_minutes ? parseFloat(formData.duration_minutes) : null
    };
    
    try {
      if (editingSong) {
        await axios.put(`${API_URL}/library/songs/${editingSong.id}`, payload, {
          headers: getAuthHeaders()
        });
        toast.success("Canción actualizada");
      } else {
        await axios.post(`${API_URL}/library/songs`, payload, {
          headers: getAuthHeaders()
        });
        toast.success("Canción agregada a la biblioteca");
      }
      
      setShowAddDialog(false);
      resetForm();
      fetchSongs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    }
  };

  const handleEdit = (song) => {
    setFormData({
      title: song.title,
      artist: song.artist || "",
      original_key: song.original_key,
      bpm: song.bpm?.toString() || "",
      time_signature: song.time_signature || "4/4",
      genre: song.genre || "Adoración",
      energy_level: song.energy_level || 5,
      notes: song.notes || "",
      lyrics: song.lyrics || "",
      duration_minutes: song.duration_minutes?.toString() || "",
      sections: song.sections || [],
      tags: song.tags || []
    });
    setEditingSong(song);
    setShowAddDialog(true);
  };

  const handleDelete = async (songId) => {
    if (!window.confirm("¿Eliminar esta canción de la biblioteca?")) return;
    
    try {
      await axios.delete(`${API_URL}/library/songs/${songId}`, {
        headers: getAuthHeaders()
      });
      toast.success("Canción eliminada");
      fetchSongs();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleDuplicate = async (song) => {
    try {
      await axios.post(`${API_URL}/library/songs`, {
        ...song,
        title: `${song.title} (copia)`
      }, {
        headers: getAuthHeaders()
      });
      toast.success("Canción duplicada");
      fetchSongs();
    } catch (error) {
      toast.error("Error al duplicar");
    }
  };

  const filteredSongs = songs.filter(song => {
    const matchesSearch = song.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (song.artist && song.artist.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesKey = filterKey === "all" || song.original_key === filterKey;
    const matchesGenre = filterGenre === "all" || song.genre === filterGenre;
    return matchesSearch && matchesKey && matchesGenre;
  });

  const getEnergyColor = (level) => {
    if (level <= 3) return "text-blue-400";
    if (level <= 6) return "text-yellow-400";
    return "text-red-400";
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <AudioWaveform className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-white font-['Manrope']">SteelMora</span>
            </Link>
            <span className="text-gray-600">/</span>
            <span className="text-white font-medium">Biblioteca</span>
          </div>
          
          <div className="flex items-center gap-3">
            <Link to="/setlists">
              <Button variant="outline" className="border-white/10 text-gray-300 gap-2">
                <ListMusic className="w-4 h-4" />
                Setlists
              </Button>
            </Link>
            <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90 btn-glow gap-2" data-testid="add-song-btn">
                  <Plus className="w-4 h-4" />
                  Nueva Canción
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#18181b] border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-white font-['Manrope']">
                    {editingSong ? "Editar Canción" : "Nueva Canción"}
                  </DialogTitle>
                  <DialogDescription className="text-gray-400">
                    Agrega detalles y estructura de la canción
                  </DialogDescription>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-300">Título *</Label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        placeholder="Nombre de la canción"
                        className="bg-black/30 border-white/10 text-white"
                        data-testid="song-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300">Artista</Label>
                      <Input
                        value={formData.artist}
                        onChange={(e) => setFormData({...formData, artist: e.target.value})}
                        placeholder="Artista o banda"
                        className="bg-black/30 border-white/10 text-white"
                      />
                    </div>
                  </div>
                  
                  {/* Musical Info */}
                  <div className="grid grid-cols-5 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-300">Tonalidad</Label>
                      <Select value={formData.original_key} onValueChange={(v) => setFormData({...formData, original_key: v})}>
                        <SelectTrigger className="bg-black/30 border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#18181b] border-white/10">
                          {ALL_KEYS.map(key => (
                            <SelectItem key={key} value={key} className="text-white">{key}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300">BPM</Label>
                      <Input
                        type="number"
                        value={formData.bpm}
                        onChange={(e) => setFormData({...formData, bpm: e.target.value})}
                        placeholder="120"
                        className="bg-black/30 border-white/10 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300">Compás</Label>
                      <Select value={formData.time_signature} onValueChange={(v) => setFormData({...formData, time_signature: v})}>
                        <SelectTrigger className="bg-black/30 border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#18181b] border-white/10">
                          {TIME_SIGNATURES.map(ts => (
                            <SelectItem key={ts} value={ts} className="text-white">{ts}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300">Duración</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={formData.duration_minutes}
                        onChange={(e) => setFormData({...formData, duration_minutes: e.target.value})}
                        placeholder="4.5"
                        className="bg-black/30 border-white/10 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300">Género</Label>
                      <Select value={formData.genre} onValueChange={(v) => setFormData({...formData, genre: v})}>
                        <SelectTrigger className="bg-black/30 border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#18181b] border-white/10">
                          {GENRES.map(genre => (
                            <SelectItem key={genre} value={genre} className="text-white">{genre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Energy Level */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-gray-300">Nivel de Energía</Label>
                      <span className={`font-mono font-bold ${getEnergyColor(formData.energy_level)}`}>
                        {formData.energy_level}/10
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-blue-400">Suave</span>
                      <Slider
                        value={[formData.energy_level]}
                        onValueChange={(v) => setFormData({...formData, energy_level: v[0]})}
                        min={1}
                        max={10}
                        step={1}
                        className="flex-1"
                      />
                      <span className="text-xs text-red-400">Intenso</span>
                    </div>
                  </div>
                  
                  {/* Sections */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-gray-300">Estructura de Secciones</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addSection} className="border-white/10 text-gray-300 gap-1">
                        <Plus className="w-3 h-3" />
                        Agregar Sección
                      </Button>
                    </div>
                    
                    {formData.sections.length === 0 ? (
                      <div className="text-center py-6 bg-black/20 rounded-lg border border-dashed border-white/10">
                        <p className="text-sm text-gray-500">No hay secciones definidas</p>
                        <p className="text-xs text-gray-600 mt-1">Agrega secciones para usar loops en ensayo</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {formData.sections.map((section, index) => (
                          <div key={index} className="flex items-center gap-3 p-3 bg-black/30 rounded-lg border border-white/5">
                            <GripVertical className="w-4 h-4 text-gray-600 cursor-move" />
                            <span className="text-gray-500 font-mono text-sm w-6">{index + 1}</span>
                            <Select 
                              value={section.name} 
                              onValueChange={(v) => updateSection(index, "name", v)}
                            >
                              <SelectTrigger className="w-32 bg-black/30 border-white/10 text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#18181b] border-white/10">
                                {SECTION_TYPES.map(type => (
                                  <SelectItem key={type} value={type} className="text-white">{type}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                value={section.bars}
                                onChange={(e) => updateSection(index, "bars", parseInt(e.target.value) || 8)}
                                className="w-16 bg-black/30 border-white/10 text-white text-center"
                              />
                              <span className="text-xs text-gray-500">compases</span>
                            </div>
                            <Input
                              value={section.notes || ""}
                              onChange={(e) => updateSection(index, "notes", e.target.value)}
                              placeholder="Notas..."
                              className="flex-1 bg-black/30 border-white/10 text-white text-sm"
                            />
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="icon"
                              onClick={() => removeSection(index)}
                              className="text-gray-500 hover:text-red-400 h-8 w-8"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Notes */}
                  <div className="space-y-2">
                    <Label className="text-gray-300">Notas para el músico</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Ej: Intro con octavas, puente más suave, final con fill..."
                      className="bg-black/30 border-white/10 text-white min-h-[80px]"
                    />
                  </div>
                  
                  {/* Lyrics */}
                  <div className="space-y-2">
                    <Label className="text-gray-300">Letra / Acordes</Label>
                    <Textarea
                      value={formData.lyrics}
                      onChange={(e) => setFormData({...formData, lyrics: e.target.value})}
                      placeholder="Puedes pegar la letra con acordes aquí..."
                      className="bg-black/30 border-white/10 text-white min-h-[100px] font-mono text-sm"
                    />
                  </div>
                  
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }} className="border-white/10 text-gray-300">
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-primary hover:bg-primary/90">
                      {editingSong ? "Guardar Cambios" : "Agregar Canción"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por título o artista..."
              className="pl-10 bg-[#18181b] border-white/10 text-white"
              data-testid="search-input"
            />
          </div>
          <Select value={filterKey} onValueChange={setFilterKey}>
            <SelectTrigger className="w-[140px] bg-[#18181b] border-white/10 text-white">
              <SelectValue placeholder="Tonalidad" />
            </SelectTrigger>
            <SelectContent className="bg-[#18181b] border-white/10">
              <SelectItem value="all" className="text-white">Todas</SelectItem>
              {ALL_KEYS.map(key => (
                <SelectItem key={key} value={key} className="text-white">{key}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterGenre} onValueChange={setFilterGenre}>
            <SelectTrigger className="w-[160px] bg-[#18181b] border-white/10 text-white">
              <SelectValue placeholder="Género" />
            </SelectTrigger>
            <SelectContent className="bg-[#18181b] border-white/10">
              <SelectItem value="all" className="text-white">Todos</SelectItem>
              {GENRES.map(genre => (
                <SelectItem key={genre} value={genre} className="text-white">{genre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#18181b] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Library className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white font-['JetBrains_Mono']">{songs.length}</p>
                  <p className="text-xs text-gray-500">Canciones</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#18181b] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                  <Music2 className="w-5 h-5 text-secondary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white font-['JetBrains_Mono']">
                    {[...new Set(songs.map(s => s.original_key))].length}
                  </p>
                  <p className="text-xs text-gray-500">Tonalidades</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#18181b] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white font-['JetBrains_Mono']">
                    {songs.filter(s => s.sections?.length > 0).length}
                  </p>
                  <p className="text-xs text-gray-500">Con estructura</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#18181b] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-gray-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white font-['JetBrains_Mono']">
                    {Math.round(songs.reduce((acc, s) => acc + (s.duration_minutes || 0), 0))}m
                  </p>
                  <p className="text-xs text-gray-500">Duración total</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Songs List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : filteredSongs.length === 0 ? (
          <Card className="bg-[#18181b] border-white/10 border-dashed">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Music2 className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2 font-['Manrope']">
                {searchQuery || filterKey !== "all" || filterGenre !== "all" 
                  ? "No hay canciones que coincidan"
                  : "Tu biblioteca está vacía"}
              </h3>
              <p className="text-gray-400 mb-6 max-w-sm mx-auto">
                {searchQuery || filterKey !== "all" || filterGenre !== "all"
                  ? "Intenta con otros filtros"
                  : "Agrega tu primera canción para empezar"}
              </p>
              {!searchQuery && filterKey === "all" && filterGenre === "all" && (
                <Button onClick={() => setShowAddDialog(true)} className="bg-primary hover:bg-primary/90 gap-2">
                  <Plus className="w-4 h-4" />
                  Agregar primera canción
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredSongs.map((song) => (
              <Card 
                key={song.id} 
                className="bg-[#18181b] border-white/10 card-hover"
                data-testid={`song-card-${song.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center flex-shrink-0">
                      <Music2 className="w-6 h-6 text-primary" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-white truncate">{song.title}</h3>
                        {song.sections?.length > 0 && (
                          <Badge variant="outline" className="text-xs bg-secondary/10 border-secondary/30 text-secondary">
                            {song.sections.length} secciones
                          </Badge>
                        )}
                        {song.notes && (
                          <FileText className="w-3 h-3 text-accent" />
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <p className="text-sm text-gray-400 truncate">
                          {song.artist || "Sin artista"}
                        </p>
                        <span className="text-gray-600">•</span>
                        <p className="text-sm text-gray-500">{song.genre}</p>
                        {song.energy_level && (
                          <>
                            <span className="text-gray-600">•</span>
                            <span className={`text-sm font-mono ${getEnergyColor(song.energy_level)}`}>
                              ⚡{song.energy_level}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <Badge className="bg-primary/20 text-primary border-primary/30 font-mono text-lg px-3 py-1">
                        {song.original_key}
                      </Badge>
                      {song.bpm && (
                        <span className="text-sm font-mono text-gray-400 hidden md:block">
                          {song.bpm} BPM
                        </span>
                      )}
                      {song.time_signature && song.time_signature !== "4/4" && (
                        <span className="text-sm font-mono text-gray-500 hidden md:block">
                          {song.time_signature}
                        </span>
                      )}
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#18181b] border-white/10">
                          <DropdownMenuItem onClick={() => handleEdit(song)} className="text-gray-300">
                            <Edit className="w-4 h-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(song)} className="text-gray-300">
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicar
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-white/10" />
                          <DropdownMenuItem onClick={() => handleDelete(song.id)} className="text-red-400">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  
                  {/* Sections preview */}
                  {song.sections?.length > 0 && (
                    <div className="mt-3 pl-16 flex items-center gap-2 overflow-x-auto">
                      {song.sections.map((section, i) => (
                        <Badge 
                          key={i} 
                          variant="outline" 
                          className={`text-xs flex-shrink-0 ${
                            section.name === "Chorus" ? "bg-secondary/10 border-secondary/30 text-secondary" :
                            section.name === "Verse" ? "bg-primary/10 border-primary/30 text-primary" :
                            section.name === "Bridge" ? "bg-accent/10 border-accent/30 text-accent" :
                            "bg-white/5 border-white/20 text-gray-400"
                          }`}
                        >
                          {section.name} ({section.bars})
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default LibraryPage;
